// ─────────────────────────────────────────────────────────────────────────────
// Design System — Extracted from design sample analysis
// Single source of truth for visual design tokens across the app
// ─────────────────────────────────────────────────────────────────────────────

import type { Persona } from '../types/survey';

// ─── Feature Tool Color Palette ──────────────────────────────────────────────
// Each of the 6 main tools gets a unique pastel card color (inspired by
// self-care/wellness grid cards in design samples)

export interface FeatureCardColors {
  /** Pastel background for the card */
  bg: string;
  /** Slightly darker bg for hover */
  bgHover: string;
  /** Icon container background — more saturated */
  iconBg: string;
  /** Icon color */
  iconColor: string;
  /** Text accent color */
  textAccent: string;
  /** Tailwind gradient for full-color variant */
  gradient: string;
  /** Shadow color for elevation */
  shadowColor: string;
}

export const FEATURE_CARD_COLORS: Record<string, FeatureCardColors> = {
  // Vivid emerald green — fresh, energetic
  checkin: {
    bg: '#ecfdf5',
    bgHover: '#d1fae5',
    iconBg: '#10b981',
    iconColor: '#ffffff',
    textAccent: '#059669',
    gradient: 'from-[#10b981] to-[#059669]',
    shadowColor: 'rgba(16, 185, 129, 0.22)',
  },
  // Vivid indigo — deep, focused
  timer: {
    bg: '#eef2ff',
    bgHover: '#e0e7ff',
    iconBg: '#6366f1',
    iconColor: '#ffffff',
    textAccent: '#4f46e5',
    gradient: 'from-[#6366f1] to-[#4f46e5]',
    shadowColor: 'rgba(99, 102, 241, 0.22)',
  },
  // Vivid orange — warm, motivating
  goals: {
    bg: '#fff7ed',
    bgHover: '#ffedd5',
    iconBg: '#f97316',
    iconColor: '#ffffff',
    textAccent: '#ea580c',
    gradient: 'from-[#f97316] to-[#ea580c]',
    shadowColor: 'rgba(249, 115, 22, 0.22)',
  },
  // Vivid violet — creative, intelligent
  coach: {
    bg: '#f5f3ff',
    bgHover: '#ede9fe',
    iconBg: '#8b5cf6',
    iconColor: '#ffffff',
    textAccent: '#7c3aed',
    gradient: 'from-[#8b5cf6] to-[#7c3aed]',
    shadowColor: 'rgba(139, 92, 246, 0.22)',
  },
  // Vivid rose/pink — growth, passion
  growth: {
    bg: '#fdf2f8',
    bgHover: '#fce7f3',
    iconBg: '#ec4899',
    iconColor: '#ffffff',
    textAccent: '#db2777',
    gradient: 'from-[#ec4899] to-[#db2777]',
    shadowColor: 'rgba(236, 72, 153, 0.22)',
  },
  // Vivid amber — warm, premium
  report: {
    bg: '#fffbeb',
    bgHover: '#fef3c7',
    iconBg: '#f59e0b',
    iconColor: '#ffffff',
    textAccent: '#d97706',
    gradient: 'from-[#f59e0b] to-[#d97706]',
    shadowColor: 'rgba(245, 158, 11, 0.22)',
  },
};

// ─── Persona Hero Gradients ──────────────────────────────────────────────────
// Full-width colored backgrounds for hero/banner areas per persona

export interface PersonaHeroStyle {
  /** CSS gradient for hero background */
  gradient: string;
  /** Lighter gradient for subtle page backgrounds */
  pageBg: string;
  /** Banner/card gradient (less intense) */
  bannerGradient: string;
  /** Text color on hero */
  heroText: string;
  /** Subtext color on hero */
  heroSubtext: string;
  /** Stat card gradient pair */
  statGradient: string;
  /** Alternative stat gradient */
  statGradientAlt: string;
}

export const PERSONA_HERO_STYLES: Record<Persona, PersonaHeroStyle> = {
  // Vivid orange → warm amber
  elementary: {
    gradient: 'linear-gradient(135deg, #f97316 0%, #fb923c 50%, #fdba74 100%)',
    pageBg: 'linear-gradient(180deg, #fff7ed 0%, #ffffff 40%)',
    bannerGradient: 'linear-gradient(135deg, #ffedd5 0%, #fff7ed 100%)',
    heroText: '#ffffff',
    heroSubtext: 'rgba(255,255,255,0.9)',
    statGradient: 'from-[#f97316] to-[#ea580c]',
    statGradientAlt: 'from-[#10b981] to-[#059669]',
  },
  // Vivid purple → violet
  secondary: {
    gradient: 'linear-gradient(135deg, #7c3aed 0%, #8b5cf6 50%, #a78bfa 100%)',
    pageBg: 'linear-gradient(180deg, #f5f3ff 0%, #ffffff 40%)',
    bannerGradient: 'linear-gradient(135deg, #ede9fe 0%, #f5f3ff 100%)',
    heroText: '#ffffff',
    heroSubtext: 'rgba(255,255,255,0.9)',
    statGradient: 'from-[#7c3aed] to-[#6d28d9]',
    statGradientAlt: 'from-[#f97316] to-[#ea580c]',
  },
  // Vivid indigo → blue
  university: {
    gradient: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 50%, #818cf8 100%)',
    pageBg: 'linear-gradient(180deg, #eef2ff 0%, #ffffff 40%)',
    bannerGradient: 'linear-gradient(135deg, #e0e7ff 0%, #eef2ff 100%)',
    heroText: '#ffffff',
    heroSubtext: 'rgba(255,255,255,0.9)',
    statGradient: 'from-[#4f46e5] to-[#4338ca]',
    statGradientAlt: 'from-[#ec4899] to-[#db2777]',
  },
  // Vivid blue → navy (premium, corporate)
  professional: {
    gradient: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)',
    pageBg: 'linear-gradient(180deg, #eff6ff 0%, #ffffff 40%)',
    bannerGradient: 'linear-gradient(135deg, #dbeafe 0%, #eff6ff 100%)',
    heroText: '#ffffff',
    heroSubtext: 'rgba(255,255,255,0.9)',
    statGradient: 'from-[#1e40af] to-[#1d4ed8]',
    statGradientAlt: 'from-[#f59e0b] to-[#d97706]',
  },
};

// ─── Design Tokens ───────────────────────────────────────────────────────────

export const RADIUS = {
  /** Small elements: badges, pills */
  sm: 'rounded-lg',       // 8px
  /** Medium elements: buttons, inputs */
  md: 'rounded-xl',       // 12px
  /** Large elements: cards */
  lg: 'rounded-2xl',      // 16px
  /** Extra large: hero cards, feature cards */
  xl: 'rounded-3xl',      // 24px
  /** Full pill shape */
  full: 'rounded-full',
} as const;

export const SHADOW = {
  /** Subtle elevation for cards at rest */
  card: 'shadow-sm',
  /** Elevated cards on hover */
  cardHover: 'shadow-lg',
  /** Feature cards with color-tinted shadows */
  colorCard: 'shadow-xl',
  /** Floating elements */
  float: 'shadow-2xl',
  /** Inset shadow for pressed states */
  inset: 'shadow-inner',
} as const;

export const SPACING = {
  /** Card internal padding - compact */
  cardSm: 'p-4',
  /** Card internal padding - default */
  cardMd: 'p-5',
  /** Card internal padding - spacious */
  cardLg: 'p-6',
  /** Section vertical padding */
  sectionY: 'py-8',
  /** Section vertical padding - large */
  sectionYLg: 'py-12',
  /** Gap between cards in grid */
  gridGap: 'gap-4',
  /** Gap between cards in grid - tight */
  gridGapSm: 'gap-3',
} as const;

// ─── Card Style Presets ──────────────────────────────────────────────────────
// Pre-composed class strings for common card patterns

export const CARD_STYLES = {
  /** Default white card */
  base: 'bg-white rounded-2xl shadow-sm border border-gray-100',

  /** White card with hover state */
  interactive: 'bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md hover:border-gray-200 transition-all duration-200 cursor-pointer',

  /** Pastel feature card (use with inline bg color) */
  pastel: 'rounded-2xl p-5 transition-all duration-200 hover:shadow-md hover:scale-[1.02] cursor-pointer',

  /** Gradient full-color card */
  gradient: 'rounded-3xl p-6 text-white shadow-xl transition-shadow duration-200 hover:shadow-2xl overflow-hidden relative',

  /** Stat card - small square */
  stat: 'rounded-2xl p-4 flex flex-col items-center justify-center text-center gap-1',

  /** Glass card for overlays */
  glass: 'bg-white/80 backdrop-blur-md rounded-2xl border border-white/50 shadow-lg',

  /** Banner/CTA card */
  banner: 'rounded-2xl p-5 relative overflow-hidden',

  /** Selected card ring */
  selected: 'ring-2 ring-offset-2 scale-[1.02]',
} as const;

// ─── Animation Presets ───────────────────────────────────────────────────────
// Framer Motion variant presets

export const ANIMATION = {
  /** Container with staggered children */
  stagger: {
    hidden: {},
    show: { transition: { staggerChildren: 0.06, delayChildren: 0.05 } },
  },

  /** Fade up entrance */
  fadeUp: {
    hidden: { opacity: 0, y: 16 },
    show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' as const } },
  },

  /** Scale in entrance */
  scaleIn: {
    hidden: { opacity: 0, scale: 0.9 },
    show: { opacity: 1, scale: 1, transition: { duration: 0.3, ease: 'easeOut' as const } },
  },

  /** Slide in from left */
  slideLeft: {
    hidden: { opacity: 0, x: -20 },
    show: { opacity: 1, x: 0, transition: { duration: 0.35, ease: 'easeOut' as const } },
  },

  /** Hover lift effect */
  hoverLift: { y: -4, transition: { duration: 0.2 } },

  /** Tap scale effect */
  tapScale: { scale: 0.97 },
} as const;

// ─── Persona Onboarding Card Colors ─────────────────────────────────────────
// Distinct pastel backgrounds for persona selection (inspired by Sample 6
// wellness cards — each category gets its own soft color)

export interface PersonaCardStyle {
  /** Pastel background */
  bg: string;
  /** Selected/active background */
  bgActive: string;
  /** Accent color for borders and text */
  accent: string;
  /** Light accent for icon background */
  iconBg: string;
  /** Ring color when selected */
  ring: string;
  /** Gradient overlay for selected state */
  selectedGradient: string;
}

export const PERSONA_CARD_STYLES: Record<Persona, PersonaCardStyle> = {
  // Vivid orange
  elementary: {
    bg: '#fff7ed',
    bgActive: '#ffedd5',
    accent: '#f97316',
    iconBg: '#fdba74',
    ring: 'ring-[#f97316]',
    selectedGradient: 'from-[#fff7ed] to-[#ffedd5]',
  },
  // Vivid purple
  secondary: {
    bg: '#f5f3ff',
    bgActive: '#ede9fe',
    accent: '#7c3aed',
    iconBg: '#c4b5fd',
    ring: 'ring-[#7c3aed]',
    selectedGradient: 'from-[#f5f3ff] to-[#ede9fe]',
  },
  // Vivid indigo
  university: {
    bg: '#eef2ff',
    bgActive: '#e0e7ff',
    accent: '#4f46e5',
    iconBg: '#a5b4fc',
    ring: 'ring-[#4f46e5]',
    selectedGradient: 'from-[#eef2ff] to-[#e0e7ff]',
  },
  // Vivid blue
  professional: {
    bg: '#eff6ff',
    bgActive: '#dbeafe',
    accent: '#2563eb',
    iconBg: '#93c5fd',
    ring: 'ring-[#2563eb]',
    selectedGradient: 'from-[#eff6ff] to-[#dbeafe]',
  },
};

// ─── Streak/Banner Styles ────────────────────────────────────────────────────
// Colored banner card styles based on streak level

export const STREAK_BANNER_STYLES = {
  /** No streak — neutral */
  none: {
    bg: 'bg-gray-50',
    border: 'border border-gray-200',
    text: 'text-gray-700',
    subtext: 'text-gray-500',
  },
  /** 1-2 days — just starting */
  starting: {
    bg: 'bg-gradient-to-r from-blue-50 to-indigo-50',
    border: 'border border-blue-200',
    text: 'text-blue-700',
    subtext: 'text-blue-500',
  },
  /** 3-6 days — building momentum */
  building: {
    bg: 'bg-gradient-to-r from-blue-500 to-indigo-500',
    border: '',
    text: 'text-white',
    subtext: 'text-white/80',
  },
  /** 7+ days — on fire */
  fire: {
    bg: 'bg-gradient-to-r from-orange-400 via-pink-500 to-rose-500',
    border: '',
    text: 'text-white',
    subtext: 'text-white/80',
  },
} as const;

export function getStreakStyle(streak: number) {
  if (streak >= 7) return STREAK_BANNER_STYLES.fire;
  if (streak >= 3) return STREAK_BANNER_STYLES.building;
  if (streak >= 1) return STREAK_BANNER_STYLES.starting;
  return STREAK_BANNER_STYLES.none;
}

// ─── Date Selector Styles ────────────────────────────────────────────────────
// Horizontal date picker inspired by Samples 3, 8, 9

export const DATE_SELECTOR = {
  /** Container */
  container: 'flex gap-2 overflow-x-auto pb-2 scrollbar-hide',
  /** Individual date item - default */
  item: 'flex flex-col items-center gap-1 px-3 py-2 rounded-xl min-w-[44px] transition-all duration-200',
  /** Active/selected date */
  itemActive: 'bg-persona-primary text-white shadow-md',
  /** Inactive date */
  itemInactive: 'bg-gray-100 text-gray-500 hover:bg-gray-200',
  /** Day label */
  dayLabel: 'text-[10px] font-medium uppercase',
  /** Date number */
  dateNumber: 'text-sm font-bold',
} as const;

// ─── Page Layout Presets ─────────────────────────────────────────────────────

export const PAGE_LAYOUT = {
  /** Standard page container */
  container: 'container mx-auto px-4 max-w-lg',
  /** Wide page container */
  containerWide: 'container mx-auto px-4 max-w-4xl',
  /** Page with bottom tab padding */
  withBottomTab: 'pb-20 md:pb-0',
  /** Page top padding (after navbar) */
  topPadding: 'pt-6',
} as const;
